import '../styles/adminResponsive.css';
import { capacityText } from '../lib/capacity';
import { usePlan } from '../components/PlanBadges';
import { isProPlan, planLabel } from '../lib/plans';
import ProfileConflict from '../components/ProfileConflict';
import { mergeProfileDraft, profileError } from '../lib/profileDraft';
import { apiErrorMessage } from '../lib/apiError';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Modal from '../components/Modal';
import { Plus, Trash2, Crown } from 'lucide-react';
import { AdminPlanControl } from '../components/AdminPlanControl';
import AdminMemberDetails, { AdminMemberDeleteDialog } from '../components/AdminMemberDetails';
import AdminDirectorySearch, { filterAdminDirectory } from '../components/AdminDirectorySearch';
import { MultiSelect, BoolSwitch, TextField, TimeRangesEditor, ConditionalAmount } from '../components/FormFields';
import { EVENT_TYPES, DAYS, TECHNICAL, DISTRICTS } from '../constants/formOptions';

const emptyForm = {
  name: '', description: '', semt: DISTRICTS[0],
  mahalle: '', acik_adres: '',
  min_capacity: '', max_capacity: '', min_capacity_flexible: false,
  accepted_event_types: [], available_days: [], available_time_ranges: [],
  has_min_spending: false, min_spending_amount: '0',
  has_fixed_rent: false, fixed_rent_amount: '0',
  has_deposit: false, deposit_amount: '0',
  cancellation_policy: '',
  technical_amenities: [],
  alcohol_allowed: false, outside_materials_allowed: false,
  quiet_event_suitable: false, table_setup_suitable: false,
  has_two_person_table_setup: false, min_table_size_60x60_or_more: false,
  has_combinable_tables: false, max_persons_per_table: '0',
  images: '',
  owner_email: '', owner_name: '',
};

const adminVenueFormValues = venue => ({ ...Object.fromEntries(Object.entries(emptyForm).map(([key, fallback]) => [key, venue[key] ?? fallback])), images: (venue.images || []).join(', ') });

const AdminVenues = () => {
  const adminPlan = usePlan(0);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [slotOpen, setSlotOpen] = useState(null);
  const [newSlot, setNewSlot] = useState('');
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchVenues(); }, []);

  const fetchVenues = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/venues`);
      setVenues(data);
    } catch (err) { toast.error('Mekân listesi yenilenemedi. Lütfen yeniden deneyin.'); }
    finally { setLoading(false); }
  };

  const handleMemberDeleted = userId => {
    setSelected(null);
    setDeleteUserId(null);
    setVenues(items => items.filter(item => item.user_id !== userId));
    fetchVenues();
  };

  const [deletingId, setDeletingId] = useState(null);
  const handleDelete = async (venue) => {
    if (!window.confirm(`"${venue.name}" mekânı silinsin mi? Bağlı başvurular, eşleşmeler ve swipe kayıtları da silinir. Sahip hesabı korunur.`)) return;
    setDeletingId(venue.id);
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/venues/${venue.id}`);
      toast.success('Mekân silindi');
      setSelected(null);
      fetchVenues();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mekân silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  const handleApproval = async (venueId, approve) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/venues/${venueId}/approve`, { approve });
      toast.success(approve ? 'Mekân onaylandı' : 'Mekân reddedildi');
      fetchVenues();
      setSelected(null);
    } catch (err) { toast.error(apiErrorMessage(err, 'Bir hata oluştu')); }  // A5: eksik DNA 422 açıklanır
  };

  const closeForm = () => {
    if (saving) return;
    setCreateOpen(false);
    setEditing(null);
    setSaveError('');
  };

  const openEdit = (venue) => {
    // Only editable fields enter the form; ownership, approval and IDs stay on the server.
    const values = Object.fromEntries(Object.entries(emptyForm).map(([key, fallback]) => [key, venue[key] ?? fallback]));
    values.images = (venue.images || []).join(', ');
    setForm(values);
    setConflict(false);
    setEditing(venue);
    setSaveError('');
    setCreateOpen(true);
  };

  const reloadDraft = async () => {
    setSaving(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/venues`);
      const fresh = data.find(v => v.id === editing.id);
      if (!fresh) throw new Error('Profil bulunamadı');
      setForm(old => mergeProfileDraft(adminVenueFormValues(editing), old, adminVenueFormValues(fresh)));
      setEditing(fresh); setVenues(data); setConflict(false); setSaveError('');
      toast.info('Güncel bilgiler getirildi; değişikliklerini kontrol edip kaydet.');
    } catch (err) { toast.error('Güncel bilgiler alınamadı; yazdıkların korundu.'); }
    finally { setSaving(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        ...form,
        min_capacity: form.min_capacity === '' ? null : parseInt(form.min_capacity), // opsiyonel (A4)
        max_capacity: parseInt(form.max_capacity),
        min_spending_amount: form.has_min_spending ? parseFloat(form.min_spending_amount || 0) : 0,
        fixed_rent_amount: form.has_fixed_rent ? parseFloat(form.fixed_rent_amount || 0) : 0,
        deposit_amount: form.has_deposit ? parseFloat(form.deposit_amount || 0) : 0,
        max_persons_per_table: parseInt(form.max_persons_per_table || 0),
        images: form.images.split(',').map(s => s.trim()).filter(Boolean),
        mahalle: form.mahalle || null,
        acik_adres: form.acik_adres || null,
        cancellation_policy: form.cancellation_policy || null,
        owner_email: form.owner_email || null,
        owner_name: form.owner_name || null,
      };
      if (editing) {
        const { owner_email, owner_name, ...update } = payload;
        update.profile_revision = editing.profile_revision ?? 0;
        const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/venues/${editing.id}`, update);
        setVenues(prev => prev.map(v => v.id === editing.id ? { ...v, ...data } : v));
        setSelected(data);
        toast.success('Mekân güncellendi. Onay durumu korundu.');
      } else {
        // A11: düğme "Oluştur ve Onayla" — onay açıkça istenir; çekirdek DNA eksikse (ör. kısa tanıtım) hiçbir kayıt açılmaz.
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/venues`, { ...payload, approve: true });
        toast.success(data?.approved ? 'Mekân oluşturuldu ve onaylandı' : 'Mekân oluşturuldu; onay bekliyor');
        fetchVenues();
      }
      setCreateOpen(false);
      setForm(emptyForm);
      setEditing(null);
    } catch (err) {
      const message = profileError(err, 'Mekân kaydedilemedi. Alanları kontrol edip tekrar dene.');
      if (err.response?.status === 409) setConflict(true);
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.trim()) return;
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/venues/${slotOpen.id}/slots`, { slot: newSlot.trim() });
      toast.success('Slot eklendi');
      setNewSlot('');
      setSlotOpen(data);
      fetchVenues();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  const handleRemoveSlot = async (slot) => {
    try {
      const { data } = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/venues/${slotOpen.id}/slots`, { data: { slot } });
      toast.success('Slot silindi');
      setSlotOpen(data);
      fetchVenues();
    } catch (err) { toast.error('Bir hata oluştu'); }
  };

  if (loading) return <div className="p-8">Yükleniyor...</div>;
  const filteredVenues = filterAdminDirectory(venues, search);

  return (
    <div className="p-8 max-w-7xl mx-auto admin-directory-page">
      <div className="mb-12 flex items-start justify-between flex-wrap gap-4 admin-directory-page-heading">
        <div>
          <p className="overline mb-2">Admin Onayı</p>
          <h1 className="text-3xl font-semibold tracking-tight" data-testid="admin-venues-title">Mekânlar</h1>
          <p className="text-sm text-muted-foreground mt-2">{venues.length} mekân kayıtlı</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(emptyForm); setSaveError(''); setConflict(false); setCreateOpen(true); }} className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em] flex items-center gap-2" data-testid="admin-new-venue-button">
          <Plus size={16} /> Yeni Mekân Ekle
        </button>
      </div>

      <AdminDirectorySearch value={search} onChange={setSearch} count={filteredVenues.length}
        total={venues.length} testId="admin-venues-search" />

      {filteredVenues.length === 0 ? (
        <div className="border border-border p-12 text-center"><p>{search.trim() ? 'Aramanızla eşleşen mekân bulunamadı.' : 'Henüz mekân kaydı bulunmuyor'}</p></div>
      ) : (
        <div className="border border-border admin-directory-list" data-testid="admin-venues-list">
          <div className="grid grid-cols-12 p-4 bg-zinc-50 border-b border-border overline text-xs admin-directory-heading">
            <div className="col-span-3">Mekân</div>
            <div className="col-span-2">İlçe</div>
            <div className="col-span-2">Kapasite</div>
            <div className="col-span-1">Etkinlik</div>
            <div className="col-span-2">Durum</div>
            <div className="col-span-2 text-right">Eylem</div>
          </div>
          {filteredVenues.map((v) => (
            <div key={v.id} className="admin-directory-entry">
              <div className="grid grid-cols-12 p-4 border-b border-border items-center hover:bg-zinc-50 transition-colors admin-directory-row admin-venue-row">
                <div className="col-span-3 font-medium flex items-center gap-2 admin-directory-name">
                  <span className="admin-directory-name-text" title={v.name}>{v.name}</span>
                  {isProPlan(v.owner_plan) && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#1E3A8A]" data-testid={`admin-venue-pro-${v.id}`}><Crown size={10} /> {planLabel(v.owner_plan)}</span>}
                  {v.owner_email && <span className="admin-directory-owner-email">{v.owner_email}</span>}
                </div>
                <div className="col-span-2 text-sm admin-directory-location"><span className="admin-mobile-label">İlçe:</span>{v.semt || '—'}</div>
                <div className="col-span-2 font-mono text-sm admin-directory-capacity"><span className="admin-mobile-label">Kapasite:</span>{capacityText(v.min_capacity, v.max_capacity, '')}{v.min_capacity_flexible ? ' · Esnek' : ''}</div>
                <div className="col-span-1 font-mono text-xs admin-directory-count"><span className="admin-mobile-label">Etkinlik türü:</span>{(v.accepted_event_types || []).length}</div>
                <div className="col-span-2 admin-directory-status">
                  <span className="admin-mobile-label">Durum:</span><span className={v.approved ? 'status-approved' : 'status-pending'}>
                    {v.approved ? 'Onaylı' : 'Beklemede'}
                  </span>{v.pending_revision && <span className="status-pill ml-1" data-testid={`admin-venue-review-${v.id}`}>İncelemede</span>}
                </div>
                <div className="col-span-2 flex justify-end gap-4 text-xs uppercase tracking-[0.1em] font-medium admin-directory-actions">
                  <button onClick={() => { setSlotOpen(v); setNewSlot(''); }} className="hover:text-[#1E3A8A] transition-colors" data-testid={`admin-venue-slots-${v.id}`}>Saatler</button>
                  <button onClick={() => setSelected(selected?.id === v.id ? null : v)} className="hover:text-[#1E3A8A] transition-colors" data-testid={`admin-venue-toggle-${v.id}`}>
                    {selected?.id === v.id ? 'Kapat' : 'Detay'} →
                  </button>
                  {v.owner_can_delete === true && <button type="button" onClick={() => setDeleteUserId(v.user_id)}
                    className="admin-member-button admin-member-danger" data-testid={`admin-venue-delete-member-${v.id}`}
                    aria-label={`${v.name}: Üyeliği sil`}>Üyeliği sil</button>}
                </div>
              </div>
              {selected?.id === v.id && (
                <>
                  <div className="px-8 pt-6 bg-zinc-50 flex items-center gap-3 flex-wrap admin-directory-plan">
                    <p className="overline">Üyelik Planı (Ücretli Mekân · Kurucu rozeti)</p>
                    <AdminPlanControl canManageProPlus={adminPlan.can_manage_pro_plus === true} venue userId={v.user_id} plan={v.owner_plan} storedPlan={v.owner_plan_stored} expiresAt={v.owner_plan_expires_at}
                      version={v.owner_plan_version} founderNumber={v.owner_founder_number} tier={v.owner_tier}
                      testId={`admin-venue-plan-${v.id}`}
                      onChanged={(patch) => setVenues(prev => prev.map(x => x.id === v.id ? { ...x, ...patch } : x))} />
                  </div>
                  <AdminMemberDetails userId={v.user_id} onDeleted={handleMemberDeleted} />
                  <VenueDetail venue={v} onEdit={() => openEdit(v)} onApprove={(a) => handleApproval(v.id, a)} onDelete={() => handleDelete(v)} deleting={deletingId === v.id} />
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {deleteUserId && <AdminMemberDeleteDialog key={deleteUserId} userId={deleteUserId}
        onClose={() => setDeleteUserId(null)} onDeleted={handleMemberDeleted} />}

      <Modal open={createOpen} onClose={closeForm} title={editing ? "Mekânı Düzenle" : "Yeni Mekân Ekle"} testId="create-venue-modal">
        <form onSubmit={handleCreate} className="space-y-6" data-testid="create-venue-form">
          <ProfileConflict visible={conflict} busy={saving} onReload={reloadDraft} />
          {saveError && <p role="alert" className="text-red-700">{saveError}</p>}
          <fieldset disabled={saving} className="space-y-6 min-w-0">
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Mekân Adı" value={form.name} onChange={(v) => setForm({...form, name: v})} testId="cv-name" />
            <div>
              <label className="overline block mb-2">İlçe</label>
              <select value={form.semt} onChange={(e) => setForm({...form, semt: e.target.value})} className="w-full px-4 py-3 border border-border bg-white focus:ring-2 focus:ring-black focus:border-black" data-testid="cv-semt">
                {DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <TextField label="Mahalle (İsteğe Bağlı)" value={form.mahalle} onChange={(v) => setForm({...form, mahalle: v})} placeholder="örn: Levent" required={false} testId="cv-mahalle" />
            <TextField label="Açık Adres (İsteğe Bağlı)" value={form.acik_adres} onChange={(v) => setForm({...form, acik_adres: v})} placeholder="Sokak, no, daire" required={false} testId="cv-acik-adres" />
            <TextField label="Min. Kapasite (opsiyonel)" type="number" value={form.min_capacity} onChange={(v) => setForm({...form, min_capacity: v})} required={false} testId="cv-min-capacity" />
            <TextField label="Maks. Kapasite" type="number" value={form.max_capacity} onChange={(v) => setForm({...form, max_capacity: v})} testId="cv-max-capacity" />
          </div>
          <BoolSwitch label="Minimum kişi sayısı esnek (küçük gruplar teyitle kabul edilebilir)" value={form.min_capacity_flexible} onChange={v => setForm({...form, min_capacity_flexible: v})} testId="cv-min-flexible" />
          <MultiSelect label="Kabul Edilen Etkinlik Türleri" options={EVENT_TYPES} selected={form.accepted_event_types} onChange={(v) => setForm({...form, accepted_event_types: v})} testId="cv-event-types" />
          <MultiSelect label="Uygun Günler" options={DAYS} selected={form.available_days} onChange={(v) => setForm({...form, available_days: v})} testId="cv-days" />
          <TimeRangesEditor label="Uygun Saat Aralıkları" ranges={form.available_time_ranges} onChange={(v) => setForm({...form, available_time_ranges: v})} testId="cv-time-ranges" />
          <div className="grid md:grid-cols-3 gap-4">
            <ConditionalAmount label="Minimum harcama var mı?" amountLabel="Minimum harcama tutarı"
              hasValue={form.has_min_spending} amountValue={form.min_spending_amount}
              onHasChange={(v) => setForm({...form, has_min_spending: v})}
              onAmountChange={(v) => setForm({...form, min_spending_amount: v})}
              testId="cv-min-spending" />
            <ConditionalAmount label="Sabit kira var mı?" amountLabel="Sabit kira tutarı"
              hasValue={form.has_fixed_rent} amountValue={form.fixed_rent_amount}
              onHasChange={(v) => setForm({...form, has_fixed_rent: v})}
              onAmountChange={(v) => setForm({...form, fixed_rent_amount: v})}
              testId="cv-fixed-rent" />
            <ConditionalAmount label="Depozito var mı?" amountLabel="Depozito tutarı"
              hasValue={form.has_deposit} amountValue={form.deposit_amount}
              onHasChange={(v) => setForm({...form, has_deposit: v})}
              onAmountChange={(v) => setForm({...form, deposit_amount: v})}
              testId="cv-deposit" />
          </div>
          <div>
            <label className="overline block mb-2">İptal şartı</label>
            <textarea value={form.cancellation_policy} onChange={(e) => setForm({...form, cancellation_policy: e.target.value})}
              className="w-full px-4 py-3 border border-border min-h-16 focus:ring-2 focus:ring-black focus:border-black"
              placeholder="örn: 7 gün önceye kadar ücretsiz iptal..."
              data-testid="cv-cancellation-policy" />
          </div>
          <MultiSelect label="Teknik İmkânlar" options={TECHNICAL} selected={form.technical_amenities} onChange={(v) => setForm({...form, technical_amenities: v})} testId="cv-technical" />
          <div className="grid md:grid-cols-2 gap-3">
            <BoolSwitch label="Alkol servisi var mı?" value={form.alcohol_allowed} onChange={(v) => setForm({...form, alcohol_allowed: v})} testId="cv-alcohol" />
            <BoolSwitch label="Dışarıdan malzeme kabul eder mi?" value={form.outside_materials_allowed} onChange={(v) => setForm({...form, outside_materials_allowed: v})} testId="cv-outside" />
            <BoolSwitch label="Sessiz etkinlik uygun mu?" value={form.quiet_event_suitable} onChange={(v) => setForm({...form, quiet_event_suitable: v})} testId="cv-quiet" />
            <BoolSwitch label="Masa düzeni uygun mu?" value={form.table_setup_suitable} onChange={(v) => setForm({...form, table_setup_suitable: v})} testId="cv-table" />
          </div>
          <div className="border-t border-border pt-4 space-y-3">
            <p className="overline">Masa Düzeni Detayları</p>
            <div className="grid md:grid-cols-2 gap-3">
              <BoolSwitch label="2 kişilik masa düzenine uygun mu?" value={form.has_two_person_table_setup} onChange={(v) => setForm({...form, has_two_person_table_setup: v})} testId="cv-two-person-table" />
              <BoolSwitch label="Minimum masa ölçüsü 60x60 cm veya üzeri mi?" value={form.min_table_size_60x60_or_more} onChange={(v) => setForm({...form, min_table_size_60x60_or_more: v})} testId="cv-min-table-size" />
              <BoolSwitch label="Birleştirilebilir masa var mı?" value={form.has_combinable_tables} onChange={(v) => setForm({...form, has_combinable_tables: v})} testId="cv-combinable-tables" />
              <TextField label="Masa başı maksimum kişi sayısı" type="number" value={form.max_persons_per_table} onChange={(v) => setForm({...form, max_persons_per_table: v})} required={false} testId="cv-max-persons-per-table" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2" htmlFor="cv-description">Açıklama (tanıtım)</label>
            <textarea id="cv-description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} aria-describedby="cv-description-rule" className="w-full px-4 py-3 border border-border min-h-20 focus:ring-2 focus:ring-black focus:border-black" data-testid="cv-description" />
            <p id="cv-description-rule" className="text-xs text-zinc-500 mt-1">Onay için en az 4 kelime ve 20 karakter.</p>
          </div>
          <TextField label="Görsel URL’leri (virgülle ayır)" value={form.images} onChange={(v) => setForm({...form, images: v})} required={false} testId="cv-images" />
          {!editing && <div className="border-t border-border pt-4">
            <p className="overline mb-3">Mekân Sahibi (İsteğe Bağlı)</p>
            <div className="grid md:grid-cols-2 gap-4">
              <TextField label="Sahip E-posta" value={form.owner_email} onChange={(v) => setForm({...form, owner_email: v})} placeholder="Boş bırakırsanız sahipsiz" required={false} testId="cv-owner-email" />
              <TextField label="Sahip İsim" value={form.owner_name} onChange={(v) => setForm({...form, owner_name: v})} required={false} testId="cv-owner-name" />
            </div>
          </div>}
          </fieldset>
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
            <button type="submit" className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em]" disabled={saving} data-testid="cv-submit">{saving ? 'Kaydediliyor…' : editing ? 'Değişiklikleri Kaydet' : 'Oluştur ve Onayla →'}</button>
            <button type="button" onClick={closeForm} disabled={saving} className="px-6 py-3 border border-border font-medium uppercase tracking-[0.1em] hover:bg-zinc-50">İptal</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!slotOpen} onClose={() => setSlotOpen(null)} title={slotOpen ? `Uygun Saatler — ${slotOpen.name}` : ''} testId="slot-modal">
        {slotOpen && (
          <div className="space-y-6">
            <form onSubmit={handleAddSlot} className="flex gap-3 admin-slot-form" data-testid="add-slot-form">
              <input type="text" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} placeholder="örn: 09:00-17:00"
                className="flex-1 px-4 py-3 border border-border bg-transparent focus:ring-2 focus:ring-black focus:border-black"
                required data-testid="new-slot-input" />
              <button type="submit" className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em] flex items-center gap-2" data-testid="new-slot-submit">
                <Plus size={16} /> Yeni Slot Ekle
              </button>
            </form>
            <div>
              <p className="overline mb-3">Mevcut Saatler ({slotOpen.available_time_ranges?.length || 0})</p>
              {!slotOpen.available_time_ranges || slotOpen.available_time_ranges.length === 0 ? (
                <p className="text-sm text-muted-foreground">Henüz slot eklenmemiş</p>
              ) : (
                <div className="border border-border">
                  {slotOpen.available_time_ranges.map((slot, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 ${i !== slotOpen.available_time_ranges.length - 1 ? 'border-b border-border' : ''} hover:bg-zinc-50`}>
                      <span className="font-mono text-sm">{slot}</span>
                      <button onClick={() => handleRemoveSlot(slot)} className="p-2 hover:bg-[#1E3A8A] hover:text-white transition-colors border border-border" data-testid={`remove-slot-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const VenueDetail = ({ venue, onApprove, onEdit, onDelete, deleting }) => (
  <div className="p-8 bg-zinc-50 border-b border-border admin-directory-detail">
    <div className="grid md:grid-cols-2 gap-8 mb-6">
      <DetailItem label="İlçe / Mahalle" value={`${venue.semt || '—'}${venue.mahalle ? ' / ' + venue.mahalle : ''}`} />
      <DetailItem label="Açık Adres" value={venue.acik_adres} />
      <DetailItem label="Kabul Edilen Etkinlik Türleri" chips={venue.accepted_event_types} />
      <DetailItem label="Uygun Günler" chips={venue.available_days} />
      <DetailItem label="Uygun Saat Aralıkları" chips={venue.available_time_ranges} mono />
      <DetailItem label="Teknik İmkânlar" chips={venue.technical_amenities} />
      <DetailItem label="Min. Harcama / Sabit Kira / Depozito" value={`${venue.has_min_spending ? venue.min_spending_amount + ' TL' : 'Yok'} / ${venue.has_fixed_rent ? venue.fixed_rent_amount + ' TL' : 'Yok'} / ${venue.has_deposit ? venue.deposit_amount + ' TL' : 'Yok'}`} mono />
      <DetailItem label="İptal Şartı" value={venue.cancellation_policy} />
      <DetailItem label="Alkol / Dışarı / Sessiz / Masa" value={`${venue.alcohol_allowed ? 'Var' : 'Yok'} • ${venue.outside_materials_allowed ? 'Evet' : 'Hayır'} • ${venue.quiet_event_suitable ? 'Uygun' : 'Değil'} • ${venue.table_setup_suitable ? 'Uygun' : 'Değil'}`} />
      <DetailItem label="Masa Detayı: 2 Kişilik / ≥60x60 / Birleştirilebilir / Maks Kişi" value={`${venue.has_two_person_table_setup ? 'Evet' : 'Hayır'} • ${venue.min_table_size_60x60_or_more ? 'Evet' : 'Hayır'} • ${venue.has_combinable_tables ? 'Var' : 'Yok'} • ${venue.max_persons_per_table || 0}`} mono />
      <DetailItem label="Açıklama" value={venue.description} fullWidth />
    </div>
    <div className="flex flex-wrap gap-3 items-center admin-directory-detail-actions">
      <button type="button" onClick={onEdit} className="px-6 py-3 border border-border font-medium hover:bg-white" data-testid={`admin-venue-edit-${venue.id}`}>Mekânı Düzenle</button>
      {!venue.approved && (
        <>
          <button onClick={() => onApprove(true)} className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em]" data-testid={`admin-venue-approve-${venue.id}`}>Onayla →</button>
          <button onClick={() => onApprove(false)} className="px-6 py-3 btn-alert font-medium uppercase tracking-[0.1em]" data-testid={`admin-venue-reject-${venue.id}`}>Reddet</button>
        </>
      )}
      <button onClick={onDelete} disabled={deleting}
        className="ml-auto px-6 py-3 border border-red-300 text-red-700 font-medium uppercase tracking-[0.1em] hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        data-testid={`admin-venue-delete-${venue.id}`}>
        <Trash2 size={14} /> {deleting ? 'Siliniyor…' : 'Yalnız mekân profilini sil'}
      </button>
    </div>
  </div>
);

const DetailItem = ({ label, value, chips, mono, fullWidth }) => (
  <div className={fullWidth ? 'md:col-span-2' : ''}>
    <p className="overline mb-2">{label}</p>
    {chips ? (
      chips && chips.length ? (
        <div className="flex flex-wrap gap-2">
          {chips.map((c, i) => <span key={i} className={`${mono ? 'font-mono' : ''} text-xs px-2 py-1 border border-border bg-white`}>{c}</span>)}
        </div>
      ) : <p className="text-sm text-muted-foreground">—</p>
    ) : (
      <p className={`${mono ? 'font-mono' : ''} text-sm`}>{value || '—'}</p>
    )}
  </div>
);

export default AdminVenues;

